import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import config from "../config";

export const LocationPickerDialog = memo(({
  isOpen,
  onClose,
  onLocationSelect,
  initialAddress,
  initialLocation,
}) => {
  const isIndiaRegion = config.isIndia;
  const countryRestriction = isIndiaRegion ? "in" : "us";
  const defaultCenter = isIndiaRegion ? { lat: 20.5937, lng: 78.9629 } : { lat: 37.0902, lng: -95.7129 };

  const [searchValue, setSearchValue] = useState(initialAddress || '');
  const [location, setLocation] = useState(initialLocation || defaultCenter);
  const [address, setAddress] = useState(initialAddress || '');
  const [area, setArea] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [country, setCountry] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  const mapRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const typingTimeoutRef = useRef(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_API_KEY || 'AIzaSyCfBZYkyctvp5zTzY-Rb1dfTYGW6TWSp7U';

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: ["places", "maps"],
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
  }, [isLoaded]);

  // Reset when dialog opens
  useEffect(() => {
    if (isOpen && initialAddress) {
      setSearchValue(initialAddress);
      setAddress(initialAddress);
      if (initialLocation?.lat) {
        setLocation(initialLocation);
      }
    }
  }, [isOpen, initialAddress, initialLocation]);

  const fetchAddress = useCallback((lat, lng) => {
    if (isLoaded && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const result = results[0];
          const addressComponents = result.address_components;
          const getComponent = (type) =>
            addressComponents.find((comp) => comp.types.includes(type))
              ?.long_name || "";

          setAddress(result.formatted_address);
          setSearchValue(result.formatted_address);
          setArea(getComponent("sublocality") || getComponent("locality"));
          setCity(getComponent("locality"));
          setStateName(getComponent("administrative_area_level_1"));
          setCountry(getComponent("country"));
        }
      });
    }
  }, [isLoaded]);

  const handleSearchChange = (value) => {
    setSearchValue(value);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (value?.length > 2 && autocompleteService.current) {
        autocompleteService.current.getPlacePredictions(
          {
            input: value,
            types: ['establishment', 'geocode'],
            componentRestrictions: { country: countryRestriction }
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setLocationSuggestions(predictions.map(prediction => ({
                description: prediction.description,
                placeId: prediction.place_id
              })));
            } else {
              setLocationSuggestions([]);
            }
          }
        );
      } else {
        setLocationSuggestions([]);
      }
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    if (suggestion && placesService.current && suggestion.placeId) {
      const request = {
        placeId: suggestion.placeId,
        fields: ['geometry', 'formatted_address', 'address_components', 'name']
      };

      placesService.current.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          setLocation({ lat, lng });
          setAddress(place.formatted_address || suggestion.description);
          setSearchValue(place.formatted_address || suggestion.description);

          if (place.address_components) {
            const getComponent = (type) =>
              place.address_components.find((comp) => comp.types.includes(type))
                ?.long_name || "";

            setArea(getComponent("sublocality") || getComponent("locality"));
            setCity(getComponent("locality"));
            setStateName(getComponent("administrative_area_level_1"));
            setCountry(getComponent("country"));
          }

          if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(15);
          }
        }
      });
      setLocationSuggestions([]);
    }
  };

  const useCurrentLocationHandler = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lng: longitude });
          fetchAddress(latitude, longitude);
        },
        (error) => {
          console.error("Error fetching location:", error);
          alert("Unable to fetch location. Please enable location services.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
    fetchAddress(lat, lng);
  };

  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
    fetchAddress(lat, lng);
  };

  const handleConfirm = () => {
    onLocationSelect({
      location,
      address,
      area,
      city,
      state: stateName,
      country
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container location-picker-dialog" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <span className="dialog-icon">📍</span>
          <span className="dialog-title">Select Location on Map</span>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="dialog-body">
          <div className="location-search-section">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search for a location..."
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="location-search-input"
              />
              {locationSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {locationSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="suggestion-item"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      {suggestion.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={useCurrentLocationHandler}
              className="location-current-btn"
            >
              📍 Use My Current Location
            </button>
          </div>

          {isLoaded ? (
            <div className="map-container">
              <GoogleMap
                mapContainerStyle={{
                  height: "400px",
                  width: "100%",
                }}
                center={location}
                zoom={12}
                onClick={handleMapClick}
                onLoad={(map) => (mapRef.current = map)}
              >
                {location.lat && location.lng && (
                  <MarkerF
                    position={location}
                    draggable
                    onDragEnd={handleMarkerDragEnd}
                  />
                )}
              </GoogleMap>
            </div>
          ) : (
            <div className="map-loading">Loading Map...</div>
          )}

          {address && (
            <div className="selected-location-info">
              <strong>Selected Location:</strong>
              <p>{address}</p>
              {city && <p className="location-city">{city}, {stateName}</p>}
            </div>
          )}
        </div>
        <div className="dialog-footer">
          <button className="dialog-cancel" onClick={onClose}>Cancel</button>
          <button className="dialog-save" onClick={handleConfirm}>Confirm Location</button>
        </div>
      </div>
    </div>
  );
});

LocationPickerDialog.displayName = 'LocationPickerDialog';